import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Routes from 'lib/utils/routes';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';
import { createActivitySetInput } from 'lib/utils/activities';
import moment from 'moment';
import { TEMP_REFS } from 'lib/constants';
import { bugsnag } from 'lib/external/bugsnag';

import { START_ACTIVITY_WITH_LABOR_TICKET } from 'lib/api/mutations';
import {
  actionCanonicalActivitySetOpen,
  actionCanonicalActivitySetDelete,
  actionSetWorkOrderOpActionLoading,
  actionSetPendingActivityTypeRef,
  actionSetLaborTicketActionLoading,
  actionCloseLaborTicketModal,
} from 'lib/actions';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import { useTranslation } from 'react-i18next';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';

const {
  ACTIVITY_REF: optimisticActivityRef,
  ACTIVITY_SET_REF: optimisticActivitySetRef,
} = TEMP_REFS;

const useStartActivityWithLaborTicket = ({ machine, workOrderOp }) => {
  const mainDispatch = useDispatch();
  const history = useHistory();
  const laborTicketEnabled = useIsLaborTicketFlow();
  const { t } = useTranslation();
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);

  const workOrderId = workOrderOp?.workOrderId;

  const operator = useSelector(getCurrentOperator);
  const activityTypes = useSelector(getActivityTypes);

  const [requestStartActivityWithLaborTicket, { loading }] = useMutation(
    START_ACTIVITY_WITH_LABOR_TICKET,
    {
      fetchPolicy: 'no-cache',
      onCompleted: async ({ startActivityWithLaborTicketResponse }) => {
        const {
          createActivitySet: {
            activitySet: {
              activities: [activity], // should be sorted start ASC
              ...activitySet
            },
          },
        } = startActivityWithLaborTicketResponse;
        await mainDispatch(
          actionCanonicalActivitySetOpen({
            activitySet,
            activity,
            optimisticActivitySetRef,
            optimisticActivityRef,
          })
        );

        mainDispatch(actionSetLaborTicketActionLoading(false));
        mainDispatch(actionCloseLaborTicketModal());

        throwSuccessToast(`Started order ${activitySet.workOrderId}`);
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        history.push(Routes.machineIdPath(machine.id));
      },
      onError: (err) => {
        // Labor ticket error
        bugsnag.notify(err, (event) => {
          event.addMetadata('user', bugsnagUserInfo);
        });
        mainDispatch(actionSetLaborTicketActionLoading(false));
        throwErrorToast(`${t('Failed to open labor ticket')}: ${err}`);

        // Activity set error
        mainDispatch(
          actionCanonicalActivitySetDelete({
            activitySetRef: optimisticActivitySetRef,
          })
        );
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        throwErrorToast('Could not start production order');
      },
    }
  );

  return async ({ activityTypeRef, partOperation, startAt }) => {
    if (!loading && laborTicketEnabled && workOrderOp && partOperation) {
      const nowISO = moment().toISOString();

      const activityMode = activityTypes.find((type) => {
        return type.activityTypeRef === activityTypeRef;
      })?.activityMode;

      mainDispatch(actionSetWorkOrderOpActionLoading(true));
      mainDispatch(actionSetLaborTicketActionLoading(true));

      const activitySetInput = createActivitySetInput({
        machine,
        workOrderOp,
        partOperation,
        activityTypeRef,
        startAt,
      });

      await requestStartActivityWithLaborTicket({
        variables: {
          input: {
            createLaborTicket: {
              clockIn: startAt || nowISO,
              clockOut: null,
              lot: workOrderOp.lot,
              sequenceNumber: workOrderOp.sequenceNumber,
              split: workOrderOp.split,
              sub: workOrderOp.sub,
              state: 'OPEN',
              resourceId: machine.erpResourceId,
              personId: operator.erpId,
              workOrderId,
              type: activityMode,
              goodParts: 0,
              badParts: 0,
            },
            createActivitySet: activitySetInput,
          },
        },
      });
    }
  };
};

export default useStartActivityWithLaborTicket;
